import { CopyOutlinedRect } from '@/assets/icons/icons';
import { CustomTitle } from '@/components';
import {
  ConfigProvider,
  Divider,
  Flex,
  message,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import React from 'react';
import type { SensorTableRow } from '../partials/sensorVitalsLayout';
import styles from './SensorsTableSection.module.css';

const { Text } = Typography;

/** Matches the 8px spacing of the other section dividers on the detail page. */
const DIVIDER_THEME = { components: { Divider: { marginLG: 8 } } };

interface SensorsTableSectionProps {
  rows: SensorTableRow[];
}

const copyText = async (trackingId: string): Promise<void> => {
  try {
    if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
    await navigator.clipboard.writeText(trackingId);
    message.success('Copied sensor ID');
  } catch (error) {
    console.error(error);
    message.error("Couldn't copy the sensor ID");
  }
};

const copySensorId = (trackingId: string): void => {
  const _ = copyText(trackingId);
};

const columns = [
  {
    title: 'Name',
    key: 'name',
    width: 180,
    render: (_: unknown, row: SensorTableRow) => (
      <Flex vertical>
        <Text className='font-12' strong>
          {row.name}
        </Text>
        {row.productNames.length > 0 && (
          <Text className={`font-12 ${styles.products}`}>
            {row.productNames.join(', ')}
          </Text>
        )}
      </Flex>
    ),
  },
  {
    title: 'Sensor ID',
    dataIndex: 'trackingId',
    key: 'trackingId',
    render: (value: string) => (
      <Text className={`font-12 ${styles.value}`}>{value}</Text>
    ),
  },
  {
    title: 'Calibration date',
    dataIndex: 'calibrationDate',
    key: 'calibrationDate',
    render: (value: string) => (
      <Text className={`font-12 ${styles.value}`}>{value}</Text>
    ),
  },
  {
    title: '',
    key: 'copy',
    width: 32,
    render: (_: unknown, row: SensorTableRow) =>
      row.trackingId === '--' ? null : (
        <Tooltip title='Copy sensor ID'>
          <button
            type='button'
            className={styles.copy}
            aria-label={`Copy sensor ID ${row.trackingId}`}
            onClick={() => copySensorId(row.trackingId)}
          >
            <CopyOutlinedRect width={16} height={16} />
          </button>
        </Tooltip>
      ),
  },
];

/** Detail page Sensors table: each sensor with the products it monitors (Figma 45497:220392). */
const SensorsTableSection: React.FC<SensorsTableSectionProps> = ({ rows }) => {
  if (!rows.length) return null;

  return (
    <>
      <Flex vertical gap={14}>
        <CustomTitle
          text='Sensors'
          fontSize={18}
          fontWeight={700}
          lineHeight='24px'
          color='var(--text-primary)'
          marginBottom='0'
        />
        <Table
          className='Shipment-details-product'
          aria-label='Sensors'
          rowKey='id'
          columns={columns}
          dataSource={rows}
          pagination={false}
          rowHoverable={false}
          size='small'
        />
      </Flex>
      <ConfigProvider theme={DIVIDER_THEME}>
        <Divider />
      </ConfigProvider>
    </>
  );
};

export default SensorsTableSection;
